import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../lib/api';
import { getTenantId } from '../lib/config';
import LandingNavbar from '../components/landing/landing-navbar';
import LandingFooter from '../components/landing/landing-footer';
import { ChevronLeft, ChevronRight, Users, Bed, X, Check, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import RoomAvailabilityView from '../components/room-availability-view';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/auth-context';

interface RoomImage {
  id: string;
  imageUrl: string;
  isPrimary: boolean;
}

interface RoomDetails {
  id: string;
  roomNumber: string;
  title?: string;
  description?: string;
  basePricePerNight: number;
  currency: string;
  maxOccupancy?: number;
  capacity?: number; // API returns capacity instead of maxOccupancy
  floor?: number;
  roomSize?: number;
  size?: string; // API also returns size as string
  bedType?: string;
  hasBalcony?: boolean;
  hasKitchen?: boolean;
  companyId: string;
  roomTypeId?: string;
  company?: {
    id: string;
    name: string;
  };
  reservations?: Array<{
    id: string;
    status: string;
    checkInDate: string;
    checkOutDate: string;
  }>;
  images: RoomImage[];
  amenities?: Array<{
    id: string;
    amenity: {
      id: string;
      name: string;
      icon?: string;
    };
  }>;
  roomTypeRef?: {
    id: string;
    name: string;
    description?: string;
    maxOccupancy?: number;
    capacity?: number;
    bedType?: string;
  };
}

export default function RoomDetailsPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [room, setRoom] = useState<RoomDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: '1',
    specialRequests: '',
    promotionId: '',
  });
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [guestId, setGuestId] = useState<string | null>(null);

  useEffect(() => {
    if (roomId) {
      loadRoomDetails();
    }
  }, [roomId]);

  const loadRoomDetails = async () => {
    try {
      setLoading(true);
      
      const tenantId = getTenantId();
      
      // Fetch available rooms from public endpoint
      const response = await fetch(`${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch accommodations: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const allRooms = data.data.rooms || [];
        
        // Find the specific room by ID
        const roomData = allRooms.find((r: any) => r.id === roomId);
        
        if (!roomData) {
          throw new Error('Room not found');
        }
        
        setRoom(roomData);
      } else {
        throw new Error('Failed to load room data');
      }
    } catch (error) {
      console.error('Failed to load room details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image: RoomImage) => {
    if (image.imageUrl.startsWith('http')) {
      return image.imageUrl;
    }
    return image.imageUrl.startsWith('/')
      ? `${apiService['baseUrl']}${image.imageUrl}`
      : `${apiService['baseUrl']}/${image.imageUrl}`;
  };

  const nextImage = () => {
    if (room && room.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % room.images.length);
    }
  };

  const prevImage = () => {
    if (room && room.images.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? room.images.length - 1 : prev - 1
      );
    }
  };

  const handleBookNow = async () => {
    if (!isAuthenticated) {
      // Store booking intent and redirect to login
      localStorage.setItem('bookingRoomId', roomId!);
      localStorage.setItem('bookingReturnUrl', window.location.pathname);
      navigate('/login');
      return;
    }

    // Find or create guest
    if (room) {
      await findOrCreateGuest(room.companyId);
    }
    
    setShowBookingModal(true);
  };

  // Find or create guest for logged-in user
  const findOrCreateGuest = async (companyId: string) => {
    if (!user) return;

    try {
      const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      };

      const userEmail = user.email && isValidEmail(user.email) ? user.email : null;

      if (userEmail) {
        const guests = await apiService.getHotelGuests({ companyId, search: userEmail });
        const existingGuest = Array.isArray(guests)
          ? guests.find((g: any) => g.email === userEmail)
          : null;

        if (existingGuest) {
          setGuestId(existingGuest.id);
          return;
        }
      }

      const firstName = (user.firstName || userEmail?.split('@')[0] || 'Guest').trim();
      const lastName = (user.lastName || '').trim() || 'User';

      const guestData: any = {
        companyId,
        firstName,
        lastName,
      };

      if (userEmail) {
        guestData.email = userEmail;
      }

      // Include phone number if available
      if (user.phone && user.phone.trim()) {
        guestData.phone = user.phone.trim();
      }

      // Include other user details if available
      if (user.country && user.country.trim()) {
        guestData.country = user.country.trim();
      }

      if (user.passportNumber && user.passportNumber.trim()) {
        guestData.passportNumber = user.passportNumber.trim();
      }

      if (user.idNumber && user.idNumber.trim()) {
        guestData.idNumber = user.idNumber.trim();
      }

      const newGuest = await apiService.createHotelGuest(guestData);
      setGuestId(newGuest.id);
    } catch (error: any) {
      console.error('Failed to find/create guest:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to set up guest profile. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleBookingSubmit = async () => {
    if (!room || !guestId) {
      toast({
        title: 'Error',
        description: 'Please wait while we set up your booking...',
        variant: 'destructive',
      });
      return;
    }

    if (!bookingForm.checkInDate || !bookingForm.checkOutDate) {
      toast({
        title: 'Validation Error',
        description: 'Please select check-in and check-out dates',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingReservation(true);
    try {
      const checkInDate = new Date(bookingForm.checkInDate).toISOString();
      const checkOutDate = new Date(bookingForm.checkOutDate).toISOString();
      const numberOfGuests = parseInt(bookingForm.numberOfGuests) || 1;

      // Check availability
      const availabilityResult = await apiService.checkRoomAvailability({
        companyId: room.companyId,
        checkInDate: bookingForm.checkInDate,
        checkOutDate: bookingForm.checkOutDate,
        roomTypeId: room.roomTypeId,
        numberOfGuests,
      });

      const isRoomAvailable = availabilityResult.rooms.some((r: any) => r.id === room.id);

      if (!isRoomAvailable || availabilityResult.rooms.length === 0) {
        toast({
          title: 'Room No Longer Available',
          description: `The selected room is no longer available for the dates ${bookingForm.checkInDate} to ${bookingForm.checkOutDate}. Please select different dates.`,
          variant: 'destructive',
        });
        setIsCreatingReservation(false);
        return;
      }

      // Calculate total amount
      const nights = Math.ceil((new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) / (1000 * 60 * 60 * 24));
      const totalAmount = room.basePricePerNight * nights;

      await apiService.createHotelReservation({
        companyId: room.companyId,
        roomId: room.id,
        guestId,
        checkInDate,
        checkOutDate,
        numberOfGuests,
        totalAmount,
        depositAmount: 0,
        specialRequests: bookingForm.specialRequests || undefined,
        promotionId: bookingForm.promotionId || undefined,
      });

      toast({
        title: 'Success',
        description: 'Reservation created successfully!',
      });

      setShowBookingModal(false);
      navigate('/my-account');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create reservation',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingReservation(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'RWF') => {
    // Ensure amount is a number
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    if (!numAmount || isNaN(numAmount)) {
      return 'Price not available';
    }
    
    if (currency === 'RWF' || currency === 'FRW') {
      return `${Math.round(numAmount).toLocaleString('en-US')} FRW`;
    }
    return `${currency} ${numAmount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <LandingNavbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading room details...</p>
          </div>
        </div>
        <div className="hidden md:block">
          <LandingFooter />
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen flex flex-col">
        <LandingNavbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-4">Room not found</p>
            <Button onClick={() => navigate('/accommodation')}>
              Back to Accommodation
            </Button>
          </div>
        </div>
        <div className="hidden md:block">
          <LandingFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <LandingNavbar />

      <main className="flex-1">
        {/* Back Button */}
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        </div>

        {/* Image Gallery */}
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Main Image */}
            <div 
              className="relative h-[400px] md:h-[500px] rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => room.images && room.images.length > 0 && setShowImageGallery(true)}
            >
              {room.images && room.images.length > 0 ? (
                <>
                  <img
                    src={getImageUrl(room.images[currentImageIndex])}
                    alt={room.title || room.roomNumber}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      console.error('🖼️  [ROOM-DETAILS] ❌ Failed to load main room image:', getImageUrl(room.images[currentImageIndex]));
                      // Remove the failed image from room state
                      setRoom((prev: any) => {
                        if (!prev || !prev.images) return prev;
                        const updatedImages = prev.images.filter((img: any, idx: number) => idx !== currentImageIndex);
                        // Adjust currentImageIndex if needed
                        if (currentImageIndex >= updatedImages.length && updatedImages.length > 0) {
                          setCurrentImageIndex(0);
                        }
                        return { ...prev, images: updatedImages };
                      });
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      View all {room.images.length} photos
                    </span>
                  </div>
                </>
              ) : (
                <div className="w-full h-full bg-gray-200 flex flex-col items-center justify-center">
                  <span className="text-gray-400 mb-2">No image available</span>
                  <span className="text-gray-300 text-sm">Room ID: {roomId}</span>
                </div>
              )}

              {/* Navigation Arrows */}
              {room.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Grid with Book Now Button */}
            <div className="grid grid-cols-2 gap-4">
              {room.images && room.images.length > 1 && room.images.slice(1, 4).map((image, index) => (
                <div
                  key={image.id}
                  className="relative h-[190px] md:h-[240px] rounded-xl overflow-hidden cursor-pointer group"
                  onClick={() => {
                    setCurrentImageIndex(index + 1);
                    setShowImageGallery(true);
                  }}
                >
                  <img
                    src={getImageUrl(image)}
                    alt={`${room.roomNumber} - ${index + 2}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      console.error('🖼️  [ROOM-DETAILS] ❌ Failed to load thumbnail image:', getImageUrl(image));
                      // Remove the failed image from room state
                      setRoom((prev: any) => {
                        if (!prev || !prev.images) return prev;
                        const updatedImages = prev.images.filter((img: any) => img.id !== image.id);
                        return { ...prev, images: updatedImages };
                      });
                    }}
                  />
                  {index === 2 && room.images.length > 4 && (
                    <div className="absolute inset-0 bg-black/10 flex items-center justify-center group-hover:bg-black/85 transition-colors">
                      <span className="text-[#d2a375] text-2xl font-bold" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                        +{room.images.length - 4} more
                      </span>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Book Now Button in 4th Grid Position */}
              <div className="relative h-[190px] md:h-[240px] rounded-xl overflow-hidden group cursor-pointer">
                {/* Background Image - Next available image (4th image or fallback) */}
                {room.images && room.images.length > 0 && (
                  <img
                    src={getImageUrl(room.images.length > 4 ? room.images[4] : room.images[room.images.length > 3 ? 3 : 0])}
                    alt="Book Now"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/webflow/IWACU-002.jpg';
                    }}
                  />
                )}
                
                {/* Semi-transparent Overlay - Darker for better text visibility */}
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors"></div>
                
                {/* Book Now Button */}
                <Button
                  onClick={handleBookNow}
                  className="absolute inset-0 w-full h-full text-xl font-bold bg-transparent hover:bg-transparent text-white border-2 border-white group-hover:border-white group-hover:scale-105 transition-all shadow-lg"
                  style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                >
                  Book Now
                </Button>
              </div>
              
              {(!room.images || room.images.length <= 1) && (
                <div className="col-span-2 h-full flex items-center justify-center text-gray-400 text-sm">
                  Additional images not available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Room Details */}
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Room Info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Title and Basic Info */}
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {room.title || room.roomTypeRef?.name || `Room ${room.roomNumber}`}
                </h1>
                <p className="text-xl text-gray-600 mb-3">Room {room.roomNumber}</p>
                
                {/* Company and Reservation Status */}
                <div className="flex flex-wrap items-center gap-4">
                  {/* Company */}
                  {room.company && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="text-[#c39d76]" size={18} />
                      <span className="font-medium">{room.company.name}</span>
                    </div>
                  )}
                  
                  {/* Reservation Status */}
                  {room.reservations && room.reservations.length > 0 && (
                    <div>
                      {room.reservations.some((r) => r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT') ? (
                        <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 text-sm font-semibold px-3 py-1 rounded-full">
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          Reserved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Available
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Show Available if no reservations */}
                  {(!room.reservations || room.reservations.length === 0) && (
                    <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Available
                    </span>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-6 py-6 border-y border-gray-200">
                {/* GUESTS */}
                {((room.capacity !== undefined && room.capacity !== null) || (room.maxOccupancy !== undefined && room.maxOccupancy !== null) || (room.roomTypeRef?.capacity !== undefined && room.roomTypeRef?.capacity !== null) || (room.roomTypeRef?.maxOccupancy !== undefined && room.roomTypeRef?.maxOccupancy !== null)) && (
                  <div className="flex items-center gap-2">
                    <Users className="text-gray-600" size={20} />
                    <span className="text-gray-700">
                      {room.capacity ?? room.maxOccupancy ?? room.roomTypeRef?.capacity ?? room.roomTypeRef?.maxOccupancy ?? 'N/A'} guests
                    </span>
                  </div>
                )}
                {/* BED TYPE */}
                {(room.bedType || room.roomTypeRef?.bedType) && (
                  <div className="flex items-center gap-2">
                    <Bed className="text-gray-600" size={20} />
                    <span className="text-gray-700">{room.bedType || room.roomTypeRef?.bedType}</span>
                  </div>
                )}
                {/* ROOM SIZE */}
                {((room.roomSize && room.roomSize > 0) || (room.size && room.size !== '')) && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700">{room.roomSize || room.size} m²</span>
                  </div>
                )}
                {/* FLOOR */}
                {(room.floor !== undefined && room.floor !== null && room.floor !== 0) && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700">Floor {room.floor}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {(room.description || room.roomTypeRef?.description) && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">About this room</h2>
                  {(() => {
                    const description = room.description || room.roomTypeRef?.description || '';
                    // Split by semicolon OR comma and filter out empty strings
                    const descriptionItems = description.split(/[;,]/).map(item => item.trim()).filter(item => item.length > 0);
                    
                    // If there's only one item, display as paragraph
                    if (descriptionItems.length === 1) {
                      return (
                        <p className="text-gray-700 leading-relaxed">
                          {descriptionItems[0]}
                        </p>
                      );
                    }
                    
                    // If multiple items, display as grid (2 columns on desktop, 1 on mobile)
                    return (
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-gray-700 leading-relaxed list-none">
                        {descriptionItems.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2 text-[#c39d76] font-bold text-lg flex-shrink-0">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )}

              {/* Amenities */}
              {room.amenities && room.amenities.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Amenities</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {room.amenities.map((item) => (
                      <div key={item.id} className="flex items-center gap-3">
                        <Check className="text-green-600" size={20} />
                        <span className="text-gray-700">{item.amenity.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
               {/* <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Features</h2>
                <div className="grid grid-cols-2 gap-4">
                  {room.hasBalcony && (
                    <div className="flex items-center gap-3">
                      <Check className="text-green-600" size={20} />
                      <span className="text-gray-700">Private Balcony</span>
                    </div>
                  )}
                  {room.hasKitchen && (
                    <div className="flex items-center gap-3">
                      <Check className="text-green-600" size={20} />
                      <span className="text-gray-700">Kitchen</span>
                    </div>
                  )}
                </div>
              </div> */}
            </div> 

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 border border-gray-200 rounded-xl p-6 bg-white">
                <div className="mb-6 flex items-baseline gap-2">
                  <div className="text-3xl font-bold text-gray-900">
                    {formatCurrency(room.basePricePerNight, room.currency)}
                  </div>
                  <div className="text-gray-600">per night</div>
                </div>

                {/* Availability Calendar - Read Only View */}
                <div className="mb-6">
                  <RoomAvailabilityView
                    roomId={room.id}
                    monthsToShow={1}
                    variant="inline"
                  />
                </div>

                {/* Book Now Button */}
                <Button
                  onClick={handleBookNow}
                  className="w-full py-6 text-lg font-bold bg-[#c39d76] hover:bg-[#b38a66] text-white transition-colors"
                >
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Full Screen Image Gallery Modal */}
      {showImageGallery && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={() => setShowImageGallery(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X size={32} />
          </button>

          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
          >
            <ChevronLeft size={32} />
          </button>

          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
          >
            <ChevronRight size={32} />
          </button>

          <img
            src={getImageUrl(room.images[currentImageIndex])}
            alt={`${room.roomNumber} - ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
            {currentImageIndex + 1} / {room.images.length}
          </div>
        </div>
      )}

      {/* Booking Form Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 sm:mb-6 md:mb-8" style={{ color: '#2C2416' }}>
              IkoroHouse Booking form
            </DialogTitle>
            {room && (
              <DialogDescription className="text-center mb-4 sm:mb-6 text-sm sm:text-base">
                Room {room.title || room.roomNumber}
              </DialogDescription>
            )}
          </DialogHeader>
          {room && (
            <form onSubmit={(e) => { e.preventDefault(); handleBookingSubmit(); }} className="space-y-4 sm:space-y-6">
              {/* ARRIVING DATE */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 md:gap-12">
                <label className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                  ARRIVING DATE:
                </label>
                <Input
                  type="date"
                  placeholder="dd/mm/yyyy"
                  value={bookingForm.checkInDate}
                  onChange={(e) => setBookingForm({ ...bookingForm, checkInDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="flex-1 bg-white border border-gray-300 rounded-none h-10 sm:h-11 text-sm sm:text-base"
                />
              </div>

              {/* DEPARTURE DATE */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 md:gap-12">
                <label className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                  DEPARTURE DATE:
                </label>
                <Input
                  type="date"
                  placeholder="dd/mm/yyyy"
                  value={bookingForm.checkOutDate}
                  onChange={(e) => setBookingForm({ ...bookingForm, checkOutDate: e.target.value })}
                  min={bookingForm.checkInDate || new Date().toISOString().split('T')[0]}
                  required
                  className="flex-1 bg-white border border-gray-300 rounded-none h-10 sm:h-11 text-sm sm:text-base"
                />
              </div>

              {/* NUMBER OF GUESTS */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 md:gap-12">
                <label className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                  NUMBER OF GUESTS:
                </label>
                <div className="flex-1 flex flex-col sm:flex-row gap-2">
                  <Input
                    type="number"
                    min="1"
                    max={room.capacity || room.maxOccupancy || room.roomTypeRef?.capacity || room.roomTypeRef?.maxOccupancy || 10}
                    value={bookingForm.numberOfGuests}
                    onChange={(e) => setBookingForm({ ...bookingForm, numberOfGuests: e.target.value })}
                    required
                    className="bg-white border border-gray-300 rounded-none h-10 sm:h-11 text-sm sm:text-base"
                  />
                  <p className="text-xs text-gray-500 mt-1 sm:mt-0 sm:ml-2 self-start sm:self-center">
                    Maximum capacity: {room.capacity || room.maxOccupancy || room.roomTypeRef?.capacity || room.roomTypeRef?.maxOccupancy || 10} {(room.capacity || room.maxOccupancy || room.roomTypeRef?.capacity || room.roomTypeRef?.maxOccupancy || 10) === 1 ? 'guest' : 'guests'}
                  </p>
                </div>
              </div>

              {/* SPECIAL REQUESTS */}
              <div className="flex flex-col md:flex-row md:items-start gap-3 sm:gap-4 md:gap-12">
                <label className="w-full md:w-48 font-semibold pt-2 text-sm sm:text-base" style={{ color: '#2C2416' }}>
                  SPECIAL REQUESTS:
                </label>
                <textarea
                  value={bookingForm.specialRequests}
                  onChange={(e) => setBookingForm({ ...bookingForm, specialRequests: e.target.value })}
                  className="flex-1 bg-white border border-gray-300 rounded-none p-2 min-h-[80px] text-sm sm:text-base"
                  placeholder="Any special requests or notes..."
                />
              </div>

              {/* PRICE BREAKDOWN */}
              {bookingForm.checkInDate && bookingForm.checkOutDate && (
                <div className="flex flex-col md:flex-row md:items-start gap-3 sm:gap-4 md:gap-12">
                  <div className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                    PRICE BREAKDOWN:
                  </div>
                  <div className="flex-1 bg-gray-50 p-4 space-y-2">
                    {(() => {
                      const nights = Math.ceil(
                        (new Date(bookingForm.checkOutDate).getTime() - new Date(bookingForm.checkInDate).getTime()) / (1000 * 60 * 60 * 24)
                      )
                      const baseAmount = (room.basePricePerNight || 0) * nights
                      const totalAmount = baseAmount

                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span>Base Price ({nights} {nights === 1 ? 'night' : 'nights'} × {room.basePricePerNight?.toLocaleString()} {room.currency || 'RWF'}):</span>
                            <span className="font-semibold">{baseAmount.toLocaleString()} {room.currency || 'RWF'}</span>
                          </div>
                          <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-300">
                            <span>Total Amount:</span>
                            <span style={{ color: '#c39d76' }}>{totalAmount.toLocaleString()} {room.currency || 'RWF'}</span>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>
              )}

              {/* SUBMIT BUTTON */}
              <div className="flex flex-col md:flex-row md:items-center gap-4 sm:gap-6 pt-4 sm:pt-6">
                <div className="w-full md:w-48 hidden md:block"></div>
                <div className="flex-1 flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBookingModal(false)}
                    className="w-full sm:flex-1 bg-white text-black font-semibold uppercase hover:opacity-90 transition-opacity py-2.5 sm:py-3 rounded-none text-sm sm:text-base"
                  >
                    CANCEL
                  </Button>
                  <Button
                    type="submit"
                    disabled={isCreatingReservation || !guestId}
                    className="w-full sm:flex-1 bg-black text-white font-semibold uppercase hover:opacity-90 transition-opacity py-2.5 sm:py-3 rounded-none text-sm sm:text-base"
                  >
                    {isCreatingReservation ? 'BOOKING...' : 'SUBMIT'}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <div className="hidden md:block">
        <LandingFooter />
      </div>
    </div>
  );
}
